"use client";

import { useState } from "react";
import { useConfirm } from "@/components/ConfirmDialog";
import { ignoreRefusal, RequestError, requestJson } from "@/lib/clientRequest";
import type { Editing, SidebarFolder } from "./types";

export interface FolderEditor {
  editing: Editing | null;
  editValue: string;
  setEditValue: (value: string) => void;
  startCreate: (parentId: string | null) => void;
  startRename: (folder: SidebarFolder) => void;
  cancel: () => void;
  commit: () => Promise<void>;
  remove: (folder: SidebarFolder) => Promise<void>;
}

/** The inline department input: what is being named, its draft, and the saves. */
export function useFolderEditing({
  activeBusiness,
  refreshAll,
  expandFolder,
}: {
  activeBusiness: string;
  /** Reloads the sidebar's lists and the server components after a change. */
  refreshAll: () => Promise<void>;
  /** Opens the parent so the new department's input is visible. */
  expandFolder: (id: string) => void;
}): FolderEditor {
  const ask = useConfirm();
  const [editing, setEditing] = useState<Editing | null>(null);
  const [editValue, setEditValue] = useState("");

  function startCreate(parentId: string | null) {
    setEditing({ mode: "create", parentId });
    setEditValue("");
    if (parentId) expandFolder(parentId);
  }

  function startRename(f: SidebarFolder) {
    setEditing({ mode: "rename", id: f.id });
    setEditValue(f.name);
  }

  function cancel() {
    setEditing(null);
  }

  async function commit() {
    if (!editing) return;
    const name = editValue.trim();
    if (!name) {
      setEditing(null);
      return;
    }
    try {
      if (editing.mode === "create")
        await requestJson("/api/folders", {
          method: "POST",
          body: {
            name,
            parentId: editing.parentId,
            businessId: activeBusiness || undefined,
          },
          fallback: "Could not save department",
        });
      else
        await requestJson(`/api/folders/${editing.id}`, {
          method: "PATCH",
          body: { name },
          fallback: "Could not save department",
        });
      setEditing(null);
      await refreshAll();
    } catch (cause) {
      if (!(cause instanceof RequestError)) throw cause;
      alert(cause.message);
    }
  }

  async function remove(f: SidebarFolder) {
    if (!(await ask({ title: `Delete department "${f.name}"?`, message: "Its sub-departments and workers move up a level.", confirmLabel: "Delete department" }))) {
      return;
    }
    await requestJson(`/api/folders/${f.id}`, { method: "DELETE" }).catch(ignoreRefusal);
    await refreshAll();
  }

  return { editing, editValue, setEditValue, startCreate, startRename, cancel, commit, remove };
}
